<template>
    <div>
        <v-snackbar v-model="snackbar" top :color="color" :timeout="6000">
            <span v-if="snackbar_msg">{{ snackbar_msg }}</span>
            <v-btn dark text @click="snackbar = false">Close</v-btn>
        </v-snackbar>

        <v-card flat class="pa-4 mt-4">
            <v-row class="my-5">
                <v-col cols="12" md="4" class="mx-auto">
                    <v-select v-model="selected_faculty" :items="faculties" item-text="faculty_name"
                        item-value="faculty_id" label="Select Faculty" outlined dense clearable @change="loadChartData"
                        :placeholder="!selected_faculty ? 'Select Faculty' : ''"></v-select>
                </v-col>
            </v-row>

            <v-row align="start" justify="center" class="py-6">
                <v-col cols="12" md="8">
                    <v-skeleton-loader v-if="loading" type="image" />
                    <chart v-else-if="working_hours_chart.series[0].data.length" class="chart" auto-resize
                        :options="working_hours_chart"></chart>
                    <div v-else class="text-center py-10 text-h6 text-grey">
                        No Working Hours Data Available.
                    </div>
                </v-col>

                <v-col cols="12" md="3" class="d-flex align-center">
                    <v-card flat class="py-5 px-3 gradient-blue fill-width text-center" rounded="lg" height="150"
                        style="margin-top: 50px;">
                        <div class="text-center">
                            <strong class="text-h6">Overall Avg Working Hours</strong>
                            <div class="text-h3 font-weight-bold">
                                {{ overallAvgWorkingHrs.toFixed(2) }}
                            </div>
                        </div>
                    </v-card>
                </v-col>
            </v-row>

            <v-divider class="my-8"></v-divider>

            <v-row align="start" justify="center" class="py-6">
                <v-col cols="12" md="8">
                    <v-skeleton-loader v-if="loading" type="image" />
                    <chart v-else-if="attendance_chart.series[0].data.length" class="chart" auto-resize
                        :options="attendance_chart"></chart>
                    <div v-else class="text-center py-10 text-h6 text-grey">
                        No Attendance Data Available.
                    </div>
                </v-col>

                <v-col cols="12" md="3" class="d-flex flex-column justify-center align-stretch">
                    <v-card flat class="py-3 mb-4 gradient-pink text-center" rounded="lg" height="100">
                        <div class="text-center">
                            <strong class="text-h6">Attendance</strong>
                            <div class="text-h3 font-weight-bold">
                                {{ attendancePercent.toFixed(2) }}%
                            </div>
                        </div>
                    </v-card>

                    <v-card flat class="py-3 gradient-dark-pink text-center" rounded="lg" height="100">
                        <div class="text-center">
                            <strong class="text-h6">Late</strong>
                            <div class="text-h3 font-weight-bold">
                                {{ latePercent.toFixed(2) }}%
                            </div>
                        </div>
                    </v-card>
                </v-col>
            </v-row>
        </v-card>
    </div>
</template>

<script>
    import axios from "axios";

    export default {
        props: ["selected_ay"],
        data() {
            const toolboxConfig = {
                show: true,
                orient: "horizontal",
                feature: {
                    magicType: {
                        show: true,
                        type: ["line", "bar"],
                        title: { line: "Line View", bar: "Bar View" },
                    },
                    dataView: {
                        show: true,
                        readOnly: true,
                        title: "Data View",
                        lang: ["Data View", "Close", "Refresh"] // ✅ fixes Chinese labels
                    },
                    restore: {
                        show: true,
                        title: "Reset Zoom",
                    },
                    saveAsImage: {
                        show: true,
                        title: "Download Image",
                    },
                },
                right: 20,
                top: 5,
            };

            return {
                snackbar: false,
                color: "",
                snackbar_msg: "",
                loading: false,
                selected_faculty: "All", // Initialize as array for multiple selections
                faculties: [],
                overallAvgWorkingHrs: 0,
                attendancePercent: 0,
                latePercent: 0,
                working_hours_chart: {
                    title: { text: "Avg Working Hours", left: "center" },
                    toolbox: toolboxConfig,
                    color: [
                        {
                            type: "linear",
                            x: 0,
                            y: 0,
                            x2: 0,
                            y2: 1,
                            colorStops: [{ offset: 0, color: "#ff0000" }, { offset: 1, color: "#cc0000" }],
                        },
                        {
                            type: "linear",
                            x: 0,
                            y: 0,
                            x2: 0,
                            y2: 1,
                            colorStops: [{ offset: 0, color: "#0000ff" }, { offset: 1, color: "#0000cc" }],
                        },
                    ],
                    tooltip: {
                        trigger: "axis",
                        formatter: (params) => {
                            let tooltip = `<strong>${params[0].name}</strong><br/>`;
                            params.forEach((item) => {
                                tooltip += `${item.marker} ${item.seriesName}: <strong>${item.value ? item.value.toFixed(2) : 0
                                    }</strong> Hrs<br/>`;
                            });
                            return tooltip;
                        },
                    },
                    legend: { bottom: 0, data: ["Avg Working Hrs", "Avg Present Hrs"] },
                    grid: { top: 80, bottom: 80 },
                    xAxis: {
                        type: "category",
                        data: [],
                        axisLabel: { rotate: 45 },
                        name: "Academic Year",
                        nameLocation: "middle",
                        nameGap: 40,
                    },
                    yAxis: { type: "value", name: "Hours", min: 0, max: 18 },
                    series: [
                        { name: "Avg Working Hrs", type: "line", data: [] },
                        { name: "Avg Present Hrs", type: "line", data: [] },
                    ],
                },
                attendance_chart: {
                    title: { text: "Attendance", left: "center" },
                    toolbox: toolboxConfig,
                    color: [
                        {
                            type: "linear",
                            x: 0,
                            y: 0,
                            x2: 0,
                            y2: 1,
                            colorStops: [{ offset: 0, color: "#0000ff" }, { offset: 1, color: "#0000cc" }],
                        },
                        {
                            type: "linear",
                            x: 0,
                            y: 0,
                            x2: 0,
                            y2: 1,
                            colorStops: [{ offset: 0, color: "#ff0000" }, { offset: 1, color: "#cc0000" }],
                        },
                        {
                            type: "linear",
                            x: 0,
                            y: 0,
                            x2: 0,
                            y2: 1,
                            colorStops: [{ offset: 0, color: "#00ff00" }, { offset: 1, color: "#00cc00" }],
                        },
                    ],
                    tooltip: {
                        trigger: "axis",
                        formatter: (params) => {
                            let tooltip = `<strong>${params[0].name}</strong><br/>`;
                            params.forEach((item) => {
                                tooltip += `${item.marker} ${item.seriesName}: <strong>${item.value}</strong> Days<br/>`;
                            });
                            return tooltip;
                        },
                    },
                    legend: { bottom: 0, data: ["Late Days", "Working Days", "Present Days"] },
                    grid: { top: 80, bottom: 80 },
                    xAxis: {
                        type: "category",
                        data: [],
                        axisLabel: { rotate: 45 },
                        name: "Academic Year",
                        nameLocation: "middle",
                        nameGap: 40,
                    },
                    yAxis: { type: "value", name: "Days", min: 0, max: 600 },
                    series: [
                        { name: "Late Days", type: "line", data: [] },
                        { name: "Working Days", type: "line", data: [] },
                        { name: "Present Days", type: "line", data: [] },
                    ],
                },
            };
        },
        mounted() {
            console.log("mounted: selected_ay =", this.selected_ay);
            if (this.selected_ay) this.loadFacultyList();
        },
        watch: {
            selected_ay(val) {
                console.log("watch: selected_ay =", val);
                if (val) this.loadFacultyList();
            },
            selected_faculty(val) {
                console.log("watch: selected_faculty =", val);
                if (!val) {
                    this.selected_faculty = "All";
                }
                if (this.selected_ay) this.loadChartData();
            },
        },
        methods: {
            showSnackbar(color, msg) {
                this.color = color;
                this.snackbar_msg = msg;
                this.snackbar = true;
            },
            async loadFacultyList() {
                if (!this.selected_ay) {
                    this.showSnackbar("error", "Please select an academic year");
                    return;
                }

                this.loading = true;
                console.log("loadFacultyList: selected_ay =", this.selected_ay);
                const payload = {
                    selected_ay: this.selected_ay,
                    selected_faculty: this.selected_faculty && this.selected_faculty !== '' ? this.selected_faculty : 'All',
                };

                try {
                    const res = await axios.post("/AdminDashboard/getAttendanceAndWorkingHoursReport", payload);
                    if (res.data.flag === "YES") {
                        // this.faculties = res.data.faculty_list.map((faculty) => ({
                        //     faculty_id: faculty.id,
                        //     faculty_name: faculty.name,
                        // })) || [];
                        // Set default to first faculty if none selected
                        this.faculties = [
                            { faculty_id: "All", faculty_name: "All" },
                            ...(res.data.faculty_list.map((faculty) => ({
                                faculty_id: faculty.id,
                                faculty_name: faculty.name,
                            })) || []),
                        ];

                        if (!this.selected_faculty) {
                            this.selected_faculty = "All";
                        }

                        // this.showSnackbar("success", "Faculty list loaded successfully");
                        // Load chart data after setting default faculty
                        await this.loadChartData();
                    } else {
                        this.showSnackbar("error", res.data.msg || "No faculty data found");
                    }
                } catch (err) {
                    console.error("Error loading faculty list:", err);
                    this.showSnackbar("error", "Failed to load faculty list");
                } finally {
                    this.loading = false;
                }
            },
            async loadChartData() {
                if (!this.selected_ay) {
                    this.showSnackbar("error", "Please select an academic year");
                    this.loading = false;
                    return;
                }

                this.loading = true;
                console.log("loadChartData: selected_ay =", this.selected_ay, "selected_faculty =", this.selected_faculty);
                const payload = {
                    selected_ay: this.selected_ay,
                    selected_faculty: this.selected_faculty && this.selected_faculty !== "" ? this.selected_faculty : "All",
                };
                console.log("Payload to be sent:", JSON.stringify(payload));
                // Reset chart data
                this.working_hours_chart.xAxis.data = [];
                this.working_hours_chart.series.forEach((s) => (s.data = []));
                this.attendance_chart.xAxis.data = [];
                this.attendance_chart.series.forEach((s) => (s.data = []));
                this.overallAvgWorkingHrs = 0;
                this.attendancePercent = 0;
                this.latePercent = 0;

                try {
                    const res = await axios.post("/AdminDashboard/getAttendanceAndWorkingHoursReport", payload);
                    if (res.data.flag === "YES") {
                        const data = res.data;
                        this.overallAvgWorkingHrs = data.overall_avg_working_hours || 0;
                        this.attendancePercent = parseFloat(data.overall_attendance_percentage) || 0;
                        this.latePercent = parseFloat(data.overall_late_percentage) || 0;
                        const whData = data.avg_working_hours_chart_data || [];
                        this.working_hours_chart.xAxis.data = whData.map((item) => item.academicYear);
                        this.working_hours_chart.series[0].data = whData.map((item) => item.avgWorkingHrs);
                        this.working_hours_chart.series[1].data = whData.map((item) => item.avgPresentHrs);
                        const attData = data.attendance_chart_data || [];
                        this.attendance_chart.xAxis.data = attData.map((item) => item.academicYear);
                        this.attendance_chart.series[0].data = attData.map((item) => item.lateEntries);
                        this.attendance_chart.series[1].data = attData.map((item) => item.workingDays);
                        this.attendance_chart.series[2].data = attData.map((item) => item.presentDays);
                        // this.showSnackbar("success", "Chart data loaded successfully");
                    } else {
                        this.showSnackbar("error", res.data.msg || "No chart data found");
                    }
                } catch (err) {
                    console.error("Error loading chart data:", err);
                    this.showSnackbar("error", "Failed to load chart data");
                } finally {
                    this.loading = false;
                }
            },
        },
    };
</script>

<style scoped>
    .chart {
        width: 100%;
        height: 400px;
        max-width: 900px;
        margin: 0 auto;
    }

    .fill-width {
        width: 100%;
    }

    .gradient-blue {
        background-color: #78ccf3;
        color: #000;
        border: 1px solid #66bef5;
    }

    .gradient-pink {
        background-color: #c577f9;
        color: #000;
        border: 1px solid #af6eec;
    }

    .gradient-dark-pink {
        background-color: #f875a9;
        color: #000;
        border: 1px solid #f384b6;
    }

    .text-center {
        text-align: center;
    }

    .gradient-blue strong,
    .gradient-pink strong,
    .gradient-dark-pink strong {
        color: #333;
    }

    h1 {
        font-size: 2rem;
        font-weight: bold;
        color: #333;
    }
</style>